import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Flex } from '@theme-ui/components';
import Cookies from 'js-cookie';

import NotFound from '@app/components/NotFound';
import { RetailerButton } from '@app/components/RetailerButton/RetailerButton';
import { COUNTRY_COOKIE } from '@app/helpers/localization';
import { pushData, pushUserData } from '@app/helpers/tracking';
import useAuthentication from '@app/hooks/useAuthentication';

import type { PricingPage_content_pricing_pages_layout_item_pricing_pages_multi_layout as MultiLayout } from '../__generated__/PricingPage';
import useLocaleWithFallback from '../../../hooks/useLocaleWithFallback';
import AddToCartErrors from '../components/AddToCartErrors';
import PricingPageHeader from '../components/PricingPageHeader/PricingPageHeader';
import ProductTile from '../components/ProductTile';
import { BottomContent } from '../SingleProduct/styles';
import { ContentTop, ContentWrapper, ExtraInformation } from './styles';

const DEFAULT_GRID_COLUMNS = 2;

const MultiProductLayout = ({ layout }: { layout: MultiLayout }) => {
  const { language } = useParams<{ language: string }>();
  const { findTranslationByKeyWithFallback } = useLocaleWithFallback();
  const { products, translations, grid_columns, show_retailer_button } = layout;

  const productIds = useMemo(
    () =>
      products
        ?.filter((product) => typeof product?.products_id !== 'undefined')
        .map((product) => product?.products_id) || [],
    [products],
  );

  const product = productIds[0];
  const { isAuthenticated, loading: isAuthenticatedLoading } =
    useAuthentication();

  useEffect(() => {
    if (isAuthenticatedLoading || !product) return;

    pushUserData({
      isAuthenticated,
      country: Cookies.get(COUNTRY_COOKIE),
      language,
      pageCategory: 'shop',
    });
    pushData({
      google_tag_params: {
        pagetype: 'product',
        pname: product?.title,
        prodid: product?.sku,
      },
    });
  }, [isAuthenticated, isAuthenticatedLoading, language, product]);

  if (!products) {
    return <NotFound errorMessage="No products found in directus" />;
  }

  const contentTop = findTranslationByKeyWithFallback({
    translations,
    key: 'content_top',
  });
  const contentBottom = findTranslationByKeyWithFallback({
    translations,
    key: 'content_bottom',
  });
  const findDealerText = findTranslationByKeyWithFallback({
    translations,
    key: 'find_dealer_text',
  });
  const extraInformation = findTranslationByKeyWithFallback({
    translations,
    key: 'extra_information',
  });

  return (
    <>
      <Box mb={5} data-testid="pricing_pages_multi_layout">
        <PricingPageHeader sku={product?.sku} />
        <AddToCartErrors />
      </Box>
      <ContentWrapper px={3}>
        {contentTop && (
          <ContentTop mb={4} dangerouslySetInnerHTML={{ __html: contentTop }} />
        )}
        {productIds.length && (
          <Flex sx={{ flexWrap: 'wrap', justifyContent: 'center' }}>
            {productIds.map(
              (product, idx) =>
                product && (
                  <Box
                    px={3}
                    mb={3}
                    key={idx}
                    sx={{
                      width: [
                        'auto',
                        `${
                          (1 / (grid_columns ?? DEFAULT_GRID_COLUMNS)) * 100
                        }%`,
                      ],
                    }}
                  >
                    <ProductTile product={product} isPrimary={idx === 0} />
                  </Box>
                ),
            )}
          </Flex>
        )}

        {show_retailer_button && (
          <Box sx={{ textAlign: 'center' }}>
            {findDealerText && <p>{findDealerText}</p>}
            <RetailerButton />
          </Box>
        )}

        {contentBottom && (
          <BottomContent dangerouslySetInnerHTML={{ __html: contentBottom }} />
        )}

        {extraInformation && (
          <ExtraInformation
            pb={3}
            dangerouslySetInnerHTML={{ __html: extraInformation }}
          />
        )}
      </ContentWrapper>
    </>
  );
};

export default MultiProductLayout;
